import React, { useState } from 'react';
import { FaCodeBranch, FaPlus, FaArrowRight, FaTrash } from 'react-icons/fa';
import { ExpandHeader, ToolCallWrapper } from '../shared/ToolCallComponents.tsx';
import { FilePathDisplay } from '../../shared/FilePathDisplay';
import { CollapsibleSection } from './shared/CollapsibleSection';

interface FileChange {
    filePath: string;
    operation: 'create' | 'delete' | 'move' | 'edit';
    originalFilePath?: string;
    content?: string;
    addedLines?: number;
    removedLines?: number;
    changes?: Array<{
        type: 'add' | 'remove';
        lines: string[];
        startLine: number;
        contextBefore: string[];
        contextBeforeStartLine: number;
        contextAfter: string[];
        contextAfterStartLine: number;
    }>;
}

interface ReviewChangesToolProps {
    item: {
        id?: string;
        name?: string;
        args?: any;
        error?: string;
        formattedFiles?: FileChange[];
        codeReview?: string;
    };
    isInProgress: boolean;
}

const FileOperationDisplay: React.FC<{ file: FileChange }> = ({ file }) => {
    const getOperationIcon = () => {
        switch (file.operation) {
            case 'create':
                return <FaPlus className="text-green-500" size={12} />;
            case 'delete':
                return <FaTrash className="text-red-500" size={12} />;
            case 'move':
                return <FaArrowRight className="text-blue-500" size={12} />;
            default:
                return <FaCodeBranch className="text-neutral-500" size={12} />;
        }
    };

    const getOperationText = () => {
        switch (file.operation) {
            case 'create':
                return 'Created';
            case 'delete':
                return 'Deleted';
            case 'move':
                return `Moved from ${file.originalFilePath}`;
            default:
                return 'Modified';
        }
    };

    if (file.operation !== 'edit') {
        return (
            <div className="flex items-center space-x-2 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-md border border-neutral-200 dark:border-neutral-700">
                {getOperationIcon()}
                <div className="flex-1">
                    <FilePathDisplay filePath={file.filePath} operation={file.operation} />
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        {getOperationText()}
                    </div>
                </div>
            </div>
        );
    }

    // Handle edit operation
    if (!file.changes || file.changes.length === 0) {
        return (
            <div className="space-y-2">
                <FilePathDisplay filePath={file.filePath} operation="edit" />
                <div className="text-neutral-500 dark:text-neutral-400 italic text-sm ml-4">
                    No changes detected
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <FilePathDisplay filePath={file.filePath} operation="edit" />
                <div className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
                    +{file.addedLines || 0} -{file.removedLines || 0} lines
                </div>
            </div>

            <div className="space-y-2">
                {file.changes.map((chunk, index) => (
                    <div key={`${file.filePath}-chunk-${index}`} className="pl-4 pr-2 py-2 mt-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-neutral-50 dark:bg-black overflow-x-auto">
                        <pre className="text-xs whitespace-pre-wrap font-mono">
                            {chunk.type === 'remove' && (
                                <div>
                                    <div className="text-red-500 dark:text-red-400 font-semibold mb-1">---</div>
                                    {(() => {
                                        let lineCounter = chunk.contextBeforeStartLine;
                                        return (
                                            <>
                                                {chunk.contextBefore.map((line, idx) => (
                                                    <div key={`before-${idx}`} className="flex">
                                                        <span className="text-neutral-400 dark:text-neutral-500 w-12 select-none">{lineCounter++}:</span>
                                                        <span className="text-neutral-600 dark:text-neutral-400">{line}</span>
                                                    </div>
                                                ))}
                                                {chunk.lines.map((line, idx) => {
                                                    const currentLine = chunk.startLine + idx;
                                                    return (
                                                        <div key={`removed-${idx}`} className="flex bg-red-50 dark:bg-red-950/20">
                                                            <span className="text-red-400 dark:text-red-500 w-12 select-none">{currentLine}:</span>
                                                            <span className="text-red-500 dark:text-red-400">{line}</span>
                                                        </div>
                                                    );
                                                })}
                                                {(() => {
                                                    let afterLineCounter = chunk.contextAfterStartLine;
                                                    return chunk.contextAfter.map((line, idx) => (
                                                        <div key={`after-${idx}`} className="flex">
                                                            <span className="text-neutral-400 dark:text-neutral-500 w-12 select-none">{afterLineCounter++}:</span>
                                                            <span className="text-neutral-600 dark:text-neutral-400">{line}</span>
                                                        </div>
                                                    ));
                                                })()}
                                            </>
                                        );
                                    })()}
                                </div>
                            )}
                            {chunk.type === 'add' && (
                                <div>
                                    <div className="text-green-600 dark:text-green-400 font-semibold mb-1">+++</div>
                                    {(() => {
                                        let lineCounter = chunk.contextBeforeStartLine;
                                        return (
                                            <>
                                                {chunk.contextBefore.map((line, idx) => (
                                                    <div key={`before-${idx}`} className="flex">
                                                        <span className="text-neutral-400 dark:text-neutral-500 w-12 select-none">{lineCounter++}:</span>
                                                        <span className="text-neutral-600 dark:text-neutral-400">{line}</span>
                                                    </div>
                                                ))}
                                                {chunk.lines.map((line, idx) => {
                                                    const currentLine = chunk.startLine + idx;
                                                    return (
                                                        <div key={`added-${idx}`} className="flex bg-green-50 dark:bg-green-950/20">
                                                            <span className="text-green-500 dark:text-green-600 w-12 select-none">{currentLine}:</span>
                                                            <span className="text-green-600 dark:text-green-400">{line}</span>
                                                        </div>
                                                    );
                                                })}
                                                {(() => {
                                                    let afterLineCounter = chunk.contextAfterStartLine;
                                                    return chunk.contextAfter.map((line, idx) => (
                                                        <div key={`after-${idx}`} className="flex">
                                                            <span className="text-neutral-400 dark:text-neutral-500 w-12 select-none">{afterLineCounter++}:</span>
                                                            <span className="text-neutral-600 dark:text-neutral-400">{line}</span>
                                                        </div>
                                                    ));
                                                })()}
                                            </>
                                        );
                                    })()}
                                </div>
                            )}
                        </pre>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ReviewChangesContent: React.FC<{item: any}> = ({ item }) => {
    const formattedFiles = item.formattedFiles;
    const codeReview = item.codeReview;

    if (!formattedFiles || formattedFiles.length === 0) {
        return (
            <div className="ml-2 text-neutral-500 dark:text-neutral-400 italic">
                No changes detected
            </div>
        );
    }

    return (
        <div className="ml-2 space-y-4">
            {/* File Changes Section - Now in a collapsible section */}
            <CollapsibleSection
                title="File Changes"
                maxHeight={500}
            >
                <div className="space-y-3">
                    {formattedFiles.map((file: FileChange) => (
                        <FileOperationDisplay key={file.filePath} file={file} />
                    ))}
                </div>
            </CollapsibleSection>

            {/* Code Review Section - Enhanced styling */}
            {codeReview && (
                <CollapsibleSection
                    title="Code Review"
                    collapseOnContent={true}
                    maxHeight={400}
                >
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                            <div className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                                {codeReview}
                            </div>
                        </div>
                    </div>
                </CollapsibleSection>
            )}
        </div>
    );
};

export const ReviewChanges: React.FC<ReviewChangesToolProps> = ({ item, isInProgress }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const formattedFiles = item.formattedFiles;
    const fileCount = formattedFiles ? formattedFiles.length : 0;

    return (
        <ToolCallWrapper>
            <div className="flex items-center justify-between w-full">
                <ExpandHeader
                    icon={<FaCodeBranch className="mr-2 text-neutral-500 dark:text-neutral-400" size={14} />}
                    title={`Review Changes${fileCount > 0 ? ` (${fileCount} files)` : ''}`}
                    isExpanded={isExpanded}
                    isInProgress={isInProgress}
                    onClick={() => setIsExpanded(prev => !prev)}
                />
            </div>
            {isExpanded && (
                <div className="mt-2">
                    {item.error && (
                        <div className="ml-2 text-red-500 dark:text-red-400 italic mb-2">
                            {item.error}
                        </div>
                    )}
                    {!item.error && <ReviewChangesContent item={item} />}
                </div>
            )}
        </ToolCallWrapper>
    );
};